import {
  View,
  Text,
  SafeAreaView,
  TouchableOpacity,
  StyleProp,
  ViewStyle,
  ScrollView,
} from 'react-native';
import React, {useEffect} from 'react';
import {_m} from 'INTL';
import NavigationBar from 'NavigationBar';
import WithdrawalManager, {WithdrawalOptions} from 'WithdrawalManager';
import SkillzSVG from 'SkillzSVG';
import * as BankWithdrawalController from 'BankWithdrawalController';
import {cardStylesFn, stylesFn} from './WithdrawalOptionsScreen.styles';
import {Badge} from 'Badge';
import {WITHDRAWAL_OPTIONS_SCREEN_CLICK} from 'AWSConstants';
import SkillzImage from 'SkillzImage';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';
import {SkillzText} from '@skillz/ui';

type WithdrawalOptionScreenProps = {
  hasBackButton?: boolean;
  onBackNavigation?: () => void;
  showPopup?: boolean;
};

export const WithdrawalOptionScreen = ({
  hasBackButton,
  onBackNavigation,
}: WithdrawalOptionScreenProps) => {
  const styles = stylesFn();

  useEffect(() => {
    WithdrawalManager.trackAWSWithdrawalEvent('WITHDRAWAL_OPTIONS_SCREEN_SHOWN', {});
    trackTelemetryEvent({
      event: TelemetryEventName.WITHDRAW_OPTIONS_SCREEN_LOAD,
      includeUserWalletDetails: true,
    });
  }, []);

  const isRTPEnabled = WithdrawalManager.isBankWithdrawalRTPEnabled();

  const optionParams = [
    {
      title: _m('Instant Bank Withdrawals'),
      subtitle: _m('(Pay out in minutes! Coming soon)'),
      iconComponent: (
        <SkillzSVG source={'icon_withdrawal_option_instant_bank.svg'} style={styles.iconStyle} />
      ),
      option: WithdrawalOptions.BANK_INSTANT_WITHDRAWAL,
      disabled: true,
      onPress: () => {
        WithdrawalManager.trackAWSWithdrawalEvent(WITHDRAWAL_OPTIONS_SCREEN_CLICK, {
          OPTION: 'BANK',
        });
        BankWithdrawalController.displayBankWithdrawalScreen({
          withdrawalType: WithdrawalOptions.BANK_INSTANT_WITHDRAWAL,
        });
        trackTelemetryEvent({
          event: TelemetryEventName.WITHDRAW_OPTIONS_SELECTION_CLICKED,
          payload: {
            cta: 'bank_instant_withdrawal_in_minutes',
          },
          includeUserWalletDetails: true,
        });
      },
      shouldShow: !isRTPEnabled,
    },
    {
      title: _m('Fast Bank Withdrawals'),
      subtitle: _m('(Usually within 24 hours)'),
      iconComponent: (
        <SkillzSVG source={'icon_withdrawal_option_fast_bank.svg'} style={styles.iconStyle} />
      ),
      option: WithdrawalOptions.BANK_DEFAULT_WITHDRAWAL,
      showBadgeWithText: _m('NEW!'),
      onPress: () => {
        WithdrawalManager.trackAWSWithdrawalEvent(WITHDRAWAL_OPTIONS_SCREEN_CLICK, {
          OPTION: 'BANK',
        });
        BankWithdrawalController.displayBankWithdrawalScreen({
          withdrawalType: WithdrawalOptions.BANK_DEFAULT_WITHDRAWAL,
        });
        trackTelemetryEvent({
          event: TelemetryEventName.WITHDRAW_OPTIONS_SELECTION_CLICKED,
          payload: {
            cta: 'bank_fast_withdrawal_within_24_hours',
          },
          includeUserWalletDetails: true,
        });
      },
      containerStyle: {},
      shouldShow: !isRTPEnabled,
    },
    {
      title: _m('Instant Bank Withdrawals'),
      subtitle: _m('Lands in minutes, 24/7* (limits apply)'),
      iconComponent: (
        <SkillzImage
          source={'icon_withdrawal_option_instant_bank_active.png'}
          style={styles.iconStyleInstantBankActive}
        />
      ),
      option: WithdrawalOptions.BANK_INSTANT_WITHDRAWAL,
      showBadgeWithText: _m('EARLY'),
      badgeStyle: {},
      onPress: () => {
        WithdrawalManager.trackAWSWithdrawalEvent(WITHDRAWAL_OPTIONS_SCREEN_CLICK, {
          OPTION: 'BANK',
        });
        BankWithdrawalController.displayBankWithdrawalScreen({
          withdrawalType: WithdrawalOptions.BANK_INSTANT_WITHDRAWAL,
        });
        trackTelemetryEvent({
          event: TelemetryEventName.WITHDRAW_OPTIONS_SELECTION_CLICKED,
          payload: {
            cta: 'bank_instant_withdrawal_in_minutes_limits_apply',
          },
          includeUserWalletDetails: true,
        });
      },
      containerStyle: {
        height: 120,
      },
      shouldShow: isRTPEnabled,
      instantUnlocked: true,
    },
    {
      title: _m('Original Payments & Checks'),
      subtitle: _m('(Up to 2 weeks + mailing time)'),
      iconComponent: (
        <SkillzSVG source={'icon_withdrawal_option_original.svg'} style={styles.iconStyle} />
      ),
      option: WithdrawalOptions.STANDARD_WITHDRAWAL,
      onPress: () => {
        WithdrawalManager.trackAWSWithdrawalEvent(WITHDRAWAL_OPTIONS_SCREEN_CLICK, {
          OPTION: 'DEFAULT',
        });
        WithdrawalManager.launchStandardWithdrawalFlow();
        trackTelemetryEvent({
          event: TelemetryEventName.WITHDRAW_OPTIONS_SELECTION_CLICKED,
          payload: {
            cta: 'standard_withdrawal',
          },
          includeUserWalletDetails: true,
        });
      },
      shouldShow: true,
    },
  ];

  const playthroughInfoText = _m(
    'Deposited funds must be played in matches before you can withdraw them. Any remaining bonus cash will be forfeit on withdrawal.'
  );

  return (
    <ScrollView contentContainerStyle={styles.scrollViewContentContainer}>
      <SafeAreaView style={styles.container}>
        <NavigationBar
          hasBackButton={hasBackButton}
          onBackButtonPress={onBackNavigation}
          title={_m('WITHDRAW')}
        />
        <View style={styles.optionContainer}>
          {isRTPEnabled ? (
            <View style={styles.rtpHeaderContainer}>
              <SkillzImage
                source={'icon_withdrawal_instant_option_header.png'}
                style={styles.iconStyleRTPHeader}
              />
              <Text style={styles.rtpHeaderText} allowFontScaling={false}>
                {_m("Select How You'd Like\nto Receive Your Funds")}
              </Text>
            </View>
          ) : null}
          {optionParams.map((option, index) => {
            if (!option.shouldShow) {
              return null;
            }

            if (option.option === WithdrawalOptions.BANK_INSTANT_WITHDRAWAL && isRTPEnabled) {
              return (
                <View>
                  <Card
                    key={index}
                    title={option.title}
                    subtitle={option.subtitle}
                    iconComponent={option.iconComponent}
                    onPress={option.onPress}
                    showBadgeWithText={option.showBadgeWithText}
                    containerStyle={option.containerStyle}
                    instantUnlocked={option.instantUnlocked}
                  />
                  <View style={styles.orLineContainer}>
                    <SkillzSVG
                      source={'icon_withdrawal_instant_or_line.svg'}
                      style={styles.iconOrLineLeft}
                    />
                    <Text style={styles.orLineText} allowFontScaling={false}>
                      {_m('OR')}
                    </Text>
                    <SkillzSVG
                      source={'icon_withdrawal_instant_or_line.svg'}
                      style={styles.iconOrLineRight}
                    />
                  </View>
                </View>
              );
            }

            return (
              <Card
                key={index}
                title={option.title}
                subtitle={option.subtitle}
                iconComponent={option.iconComponent}
                onPress={option.onPress}
                showBadgeWithText={option.showBadgeWithText}
                containerStyle={option.containerStyle}
                instantUnlocked={option.instantUnlocked}
                badgeStyle={option.badgeStyle}
                disabled={option.disabled}
              />
            );
          })}
        </View>
        <View style={styles.playthroughInfoContainer}>
          <SkillzSVG source={'italic_info_icon_blue.svg'} style={styles.playthroughInfoIcon} />
          <View style={styles.playthroughInfoTextContainer}>
            <SkillzText style={styles.playthroughInfoTitle}>{_m('Info')}</SkillzText>
            <SkillzText style={styles.playthroughInfoText}>{playthroughInfoText}</SkillzText>
          </View>
        </View>
      </SafeAreaView>
    </ScrollView>
  );
};

type CardProps = {
  title: string;
  subtitle: string;
  iconComponent: JSX.Element;
  onPress: () => void;
  disabled?: boolean;
  showBadgeWithText?: string;
  containerStyle?: StyleProp<ViewStyle>;
  instantUnlocked?: boolean;
  badgeStyle?: StyleProp<ViewStyle>;
};
const Card = ({
  title,
  subtitle,
  iconComponent,
  onPress,
  disabled = false,
  showBadgeWithText = '',
  containerStyle,
  instantUnlocked = false,
  badgeStyle,
}: CardProps) => {
  const styles = cardStylesFn(disabled);
  return (
    <TouchableOpacity
      style={[styles.container, containerStyle]}
      onPress={onPress}
      disabled={disabled}
    >
      <View style={{flex: 1}}>
        <View style={styles.contentContainer}>
          {iconComponent}
          <View style={[styles.textContainer, instantUnlocked && {marginRight: 10}]}>
            <Text style={styles.title} allowFontScaling={false}>
              {title}
            </Text>
            <Text style={styles.secondaryText} allowFontScaling={false}>
              {subtitle}
            </Text>
          </View>
          <View style={styles.rightArrowContainer}>
            <SkillzSVG
              source='icon_arrow_right.svg'
              style={styles.arrowIconStyle}
              tintColor={disabled ? '#C7C7C7' : '#367BCE'}
            />
          </View>
        </View>
        {instantUnlocked && (
          <View style={styles.instantUnlockedContainer}>
            <SkillzImage
              source={'icon_withdrawal_instant_unlocked_check.png'}
              style={styles.instantUnlockedIcon}
            />
            <Text style={styles.instantUnlockedText} allowFontScaling={false}>
              {_m('INSTANT UNLOCKED')}
            </Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};
